import { state } from './state.js';
import { createDonutChart, getDonutChartDataUrl } from './charts.js';
import { outputModalData } from './modals.js';
import { updateSideBar } from './sidebar.js';
import { generateFilterPill, cleanupUnusedPills } from './ui.js';
import _ from 'lodash';

/**
 * Handle cluster clicks
 */
export function clusterClickHandler(event, cluster, map) {
	map.fitBounds(cluster.bounds, 80);
}

/**
 * Add marker to category filter
 */
export function addMarkerCategoryFilter(id = null) {
	if (id === null) {
		return;
	}
	if (Array.isArray(id)) {
		state.filterCategories = id;
	} else {
		if (state.filterCategories.includes(id)) {
			state.filterCategories = state.filterCategories.filter(item => item !== id);
		} else {
			state.filterCategories.push(id);
		}
	}
	updateFilteredMarkers();
}

/**
 * Remove marker category filter
 */
export function removeMarkerCategoryFilter(id = null) {
	if (id === null) {
		state.filterCategories = [];
	} else {
		state.filterCategories = state.filterCategories.filter(item => item !== id);
	}
	updateFilteredMarkers();
}

/**
 * Toggle all markers on
 */
export function toggleAllMarkersOn() {
	state.filterCategories = [];
	cleanupUnusedPills();
	state.markers.forEach(marker => {
		marker.map = null;
	});
	state.markerCluster.clearMarkers();
	state.markerCluster.addMarkers(state.markers);
}

/**
 * Toggle all markers off
 */
export function toggleAllMarkersOff() {
	state.markers.forEach(marker => {
		marker.map = null;
	});
	state.markerCluster.clearMarkers();
}

/**
 * Update the Filtered Markers
 */
export function updateFilteredMarkers() {
	const toggledSet = new Set();
	const categoryIds = state.filterCategories.map(id => parseInt(id));

	const pillBar = document.getElementById('pill-bar');

	state.filterCategories.forEach(element => {
		const checkedTarget = document.querySelector(`#filters input[type="checkbox"][value="${element}"], #filters input[type="checkbox"][data-term-id="${element}"]`);
		if (checkedTarget) {
			checkedTarget.checked = true;
			const color = checkedTarget.dataset.pillColor;
			const title = checkedTarget.dataset.pillTitle || checkedTarget.id;
			const existingPill = pillBar?.querySelector(`.cat-pill button[data-term-id='${element}']`);

			if (pillBar && !existingPill) {
				const pill = generateFilterPill(title, element, color);
				pillBar.appendChild(pill);
			}
		}
	});

	cleanupUnusedPills();

	if (categoryIds.length > 0) {
		state.markers.forEach(marker => {
			const subcats = marker.properties.child_category_id;
			const cats = marker.properties.category_id;

			const matchFound = (Array.isArray(subcats)
				? subcats.some(sc => categoryIds.includes(parseInt(sc)))
				: categoryIds.includes(parseInt(subcats))) ||
				(Array.isArray(cats)
				? cats.some(c => categoryIds.includes(parseInt(c)))
				: categoryIds.includes(parseInt(cats)));

			if (matchFound) {
				toggledSet.add(marker);
			}
		});

		// Use the same plotting function as search for consistent bounds and behavior
		showOnlyMarkers(Array.from(toggledSet).map(m => ({ item: m })));
	} else {
		toggleAllMarkersOn();
	}
}

/**
 * Search markers using Fuse.js
 */
export function searchMarkers(term = null) {
	if (term === null) {
		return;
	}
	
	const options = {
		includeScore: true,
		threshold: 0.4,
		location: 0,
		distance: 100,
		maxPatternLength: 32,
		minMatchCharLength: 2,
		shouldSort: true,
		keys: [
			{ name: 'title', weight: 1.0 },
			{ name: 'name', weight: 1.0 },
			{ name: 'properties.name', weight: 1.0 },
			{ name: 'properties.keywords', weight: 0.8 },
			{ name: 'properties.category_title', weight: 0.5 },
			{ name: 'description', weight: 0.3 }
		]
	};
	
	// Fuse is global
	const fuse = new window.Fuse([...state.markers, ...state.onlineOnlyBusinesses.businesses], options);
	let results = fuse.search(term);
	
	// Exact word match filter to prevent "Ice" matching "Service", "Office", etc.
	// We split the term to ensure each word matches as a whole word somewhere in the fields
	const words = term.split(/\s+/).filter(w => w.length >= 2);
	const regexes = words.map(w => new RegExp(`\\b${w}\\b`, 'i'));
	
	results = results.filter(result => {
		const item = result.item;
		const props = item.properties || {};
		const searchFields = [
			item.title,
			item.description,
			item.name,
			props.name,
			props.category_title,
			props.keywords,
			props.category_id,
			props.child_category_id
		];
		
		// Ensure every word in the search term matches at least one field
		return regexes.every(regex => {
			return searchFields.some(field => {
				if (!field) return false;
				if (Array.isArray(field)) {
					return field.some(val => regex.test(String(val)));
				}
				return regex.test(String(field));
			});
		});
	});

	results.sort((a, b) => {
		const aIsMember = a.item.is_chamber_member || a.item.properties?.is_chamber_member || false;
		const bIsMember = b.item.is_chamber_member || b.item.properties?.is_chamber_member || false;

		// If one is a member and the other isn't, member comes first
		if (aIsMember && !bIsMember) {
			return -1;
		}
		if (!aIsMember && bIsMember) {
			return 1;
		}

		// If both are members or both are not, sort by score
		return a.score - b.score;
	});
	
	return results;
}

/**
 * Show only specific markers
 */
export function showOnlyMarkers(markers = null) {
	if (markers === null) {
		return;
	}
	toggleAllMarkersOff();
	
	const bounds = new google.maps.LatLngBounds();
	let tilt = state.map.getTilt();
	if (tilt <= 65) {
		tilt = 65;
	}
	let heading = state.map.getHeading();

	const markersToAdd = [];
	markers.forEach(marker => {
		// results from Fuse have the marker in 'item'
		const m = marker.item;
		if (m && m.position) {
			m.map = null;
			markersToAdd.push(m);
			bounds.extend(m.position);
		}
	});
	
	state.markerCluster.addMarkers(markersToAdd);
	
	// Fit map to bounds of filtered markers
	if (!bounds.isEmpty()) {
		state.map.fitBounds(bounds, 160);
		state.map.setTilt(tilt);
		state.map.setHeading(heading);
	}
}

/**
 * Check if marker is in viewport bounds
 */
export function check_in_bounds(marker) {
	if (!state.map || !marker || !marker.position) {
		return false;
	}
	const bounds = state.map.getBounds();
	if (!bounds) {
		return false;
	}
	return bounds.contains(marker.position);
}

/**
 * Default Marker Renderer for Clusterer
 */
export const renderMarkers = {
    render: function ({markers, count, position}, stats) {
        const reducedMarkers = _.groupBy(markers, function (n) {
            const {category_id, child_category_id} = n.properties;
            if (child_category_id !== 0) {
                return child_category_id;
            } else {
                return category_id;
            }
        });
        let counts = [];
        for (const [key, value] of Object.entries(reducedMarkers)) {
            counts.push({cat_id: key, count: value.length, color: value[0].properties.color});
        }
        
        // Use AdvancedMarkerElement for clusters
        const img = document.createElement('img');
        img.src = getDonutChartDataUrl(counts);
        img.style.width = '30px';
        img.style.height = '30px';
        img.alt = '';

        return new google.maps.marker.AdvancedMarkerElement({
            position,
            content: img,
            title: `${count} grouped businesses`,
            zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
        });
    }
};
